"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { api } from "~/trpc/react";
import { Button } from "~/components/ui/button";
import { DemoCTA } from "~/components/siteglue/siteglue-cta";
import { MoonlightAgentCard } from "~/app/components/moonlight-marketplace/moonlight-agent-card";
import { type MoonlightAgentListItem } from "~/lib/marketplace/moonlight-home";
import { SITEGLUE_CTA_CONFIG, SITEGLUE_LIVE_DEMO_GALLERY_ROUTE } from "~/lib/siteglue/cta-config";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";

const FEATURED_AGENT_LIMIT = 8;

export function FeaturesAllAgentsSection() {
  const chrome = getMarketingChrome();
  const isSiteGlue = chrome.brandId === "siteglue";
  const agentsQuery = api.project.getStaffingAgents.useQuery({});
  const agents = ((agentsQuery.data?.agents ?? []) as MoonlightAgentListItem[]).slice(
    0,
    FEATURED_AGENT_LIMIT,
  );

  return (
    <section id="agents" data-testid="section-all-agents" className="relative py-20 sm:py-28">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="text-center sm:text-left">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              {isSiteGlue ? (
                <>
                  Live AI <span className="text-primary">Employees</span>
                </>
              ) : (
                <>
                  All {chrome.agentProductName} <span className="text-primary">Agents</span>
                </>
              )}
            </h2>
            <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
              {isSiteGlue
                ? "Explore live demos across industries and roles—then create your own AI employee."
                : "Browse official templates and Moonlight agents from real practices. Try live chat and phone demos, then hire and personalize for your business."}
            </p>
          </div>
          {isSiteGlue ? (
            <DemoCTA
              location="feature_section"
              size="lg"
              className="shrink-0 self-center sm:self-auto"
              label={SITEGLUE_CTA_CONFIG.exploreLiveEmployees.label}
              href={SITEGLUE_LIVE_DEMO_GALLERY_ROUTE}
            />
          ) : (
            <Link href="/agents" className="shrink-0 self-center sm:self-auto">
              <Button variant="outline" size="lg" className="gap-2">
                Explore Agents
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          )}
        </div>

        {agentsQuery.isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-72 animate-pulse rounded-xl border bg-muted/40" />
            ))}
          </div>
        ) : agents.length === 0 ? (
          <div className="rounded-xl border border-dashed py-16 text-center">
            <p className="text-lg font-medium">Agents coming soon</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Check back for healthcare AI templates you can hire and deploy.
            </p>
            <Link href="/agents" className="mt-6 inline-block">
              <Button>Browse agents</Button>
            </Link>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {agents.map((agent) => (
              <MoonlightAgentCard key={agent.id} agent={agent} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
